import React from 'react';
import { useLazyQuery } from '@apollo/client';
import { message } from 'antd';
import { CasesSendReportV1CoverLetterDocumentPickerContainerSendReportCaseV1Document } from '../../../../../../generated';
import { pdf } from '@react-pdf/renderer';
import PdfEpicReportInfoPage from '../../../../../shared/pdf-generation/pdf-pages/pdf-verification-report-info-page';
import { AuthResult, AzureBlobResponse } from '../../../../../shared/file-upload/azure-upload';
import { CoverLetterDocumentPicker } from './cover-letter-document-picker';
import dayjs from 'dayjs';
import { FormatDate } from '../../../../../../constants';


interface CoverLetterDocumentPickerContainerProps {
  authorizeRequest: (file: File) => Promise<AuthResult>;
  onSuccess: (response: AzureBlobResponse) => Promise<void>;
  caseId: string;
  onCancel: () => void;
  onStartPdfGeneration: () => void;
  isProcessing: boolean;
  isProcessingUploadingFile?: boolean;
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const CoverLetterDocumentPickerContainer: React.FC<CoverLetterDocumentPickerContainerProps> = (props) => {
  const onSuccess = async (response: AzureBlobResponse) => {
    await props.onSuccess(response);
    message.success('Cover Letter generated successfully');
    props.setIsEditing && props.setIsEditing(false);
  };

  const [getPdfData, { loading: caseLoading }] = useLazyQuery(CasesSendReportV1CoverLetterDocumentPickerContainerSendReportCaseV1Document, {
    variables: {
      sendReportCaseV1Id: props.caseId
    },
    fetchPolicy: 'network-only'
  });

  const getGenerateCoverLetterData = async (otherData: string[], photoUrl: string) => {
    props.onStartPdfGeneration();
    const pdfData = await getPdfData();
    const caseData = pdfData.data?.sendReportCaseV1;
    const dateOfIssued = dayjs().format('D MMMM YYYY');
    const formattedDateOfBirth = FormatDate(caseData?.applicant?.personalInformation?.identityDetails?.dateOfBirth, 'D MMMM YYYY');


    const combinedData = {
      dateOfBirth: formattedDateOfBirth,
      dateOfIssued: dateOfIssued,
      gender: (() => {
        const genderMap = { M: 'Male', F: 'Female' };
        const rawGender = caseData?.applicant?.personalInformation?.identityDetails?.gender;
        return rawGender ? genderMap[rawGender as keyof typeof genderMap] || rawGender : '';
      })(),    
      caseId: caseData?.id,
      applicantId: caseData?.applicant?.id,
      nameOnCredential: caseData?.caseDetails?.application?.credential?.caseDetails?.application?.nameOnCredential,
      credentialType: caseData?.caseDetails?.application?.credential?.caseDetails?.application?.credentialType,
      includedFiles: otherData,
      lastName: caseData?.applicant?.personalInformation?.identityDetails?.lastName,
      restOfName: caseData?.applicant?.personalInformation?.identityDetails?.restOfName,
      photo: photoUrl,
      issuingInstitution: caseData?.caseDetails?.application?.credential?.caseDetails?.application?.issuingInstitution?.entityName, 
      sendDestination: caseData?.caseDetails?.application?.credential?.caseDetails?.application?.sendDestination?.entityName,
      sendDestinationAddressLine1: caseData?.caseDetails?.application?.credential?.caseDetails?.application?.sendDestination?.client?.address?.addressLine1,
      sendDestinationAddressLine2: caseData?.caseDetails?.application?.credential?.caseDetails?.application?.sendDestination?.client?.address?.addressLine2,
      sendDestinationcity: caseData?.caseDetails?.application?.credential?.caseDetails?.application?.sendDestination?.client?.address?.city,
      sendDestinationState: caseData?.caseDetails?.application?.credential?.caseDetails?.application?.sendDestination?.client?.address?.stateOrProvince,
      sendDestinationZipCode: caseData?.caseDetails?.application?.credential?.caseDetails?.application?.sendDestination?.client?.address?.zipOrPostalCode,
      sendDestinationCountry: caseData?.caseDetails?.application?.credential?.caseDetails?.application?.sendDestination?.client?.address?.country,
      statusOfCredential: caseData?.caseDetails?.application?.credential?.caseDetails?.applicationReview?.privateCaseDetails?.verificationStatus?.verificationResponse,
      generationalSuffix: caseData?.applicant?.personalInformation?.identityDetails?.generationalSuffix
    };

    return { combinedData };
  };


  const generateIdFormPdf = async (data: any): Promise<File> => {
    if (!data.dateOfBirth || !data.gender || !data.lastName || !data.caseId || !data.applicantId) {
      throw new Error('Missing data to generate cover letter');
    }

    const pdfBlob = await pdf(<PdfEpicReportInfoPage data={data} />).toBlob();
    return new File([pdfBlob], 'cover-letter.pdf', { type: 'application/pdf' });
  };

  const handleCancel = () => {
    props.onCancel();
  };
  return (
    <CoverLetterDocumentPicker
      onCancel={handleCancel}
      generateCoverLetterPdf={generateIdFormPdf}
      generateCoverLetterPdfData={getGenerateCoverLetterData}
      isProcessingUploadingFile={props.isProcessingUploadingFile}
      isProcessing={props.isProcessing || caseLoading}
      authorizeRequest={props.authorizeRequest}
      onSuccess={onSuccess}
    />
  );
};
